import TutorialCard from "components/Tutorials/InstantPayTutorial.tsx/TutorialCard";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ScanIcon } from "assets/scan.svg";
import { ReactComponent as WithDrawMoneyIcon } from "assets/withdraw_money.svg";
import { ReactComponent as MoneyIcon } from "assets/money.svg";
import { ReactComponent as PaymentIcon } from "assets/payment.svg";
import StepIndicators from "components/Tutorials/InstantPayTutorial.tsx/StepIndicators";
import useIsMobile from "hooks/useIsMobile";

const ICON_WIDTH = "80px";
const ICON_HEIGHT = "80px";
export type HowInstantPayWorksType = {
  id: number;
  title: string;
  icon: React.ReactNode;
  note: string;
  onNext: () => void;
  description: string;
};

interface IHowInstantPayWorksProps {
  setLastCompletedStep: (id: number) => void;
}

const HowInstantPayWorks: React.FC<IHowInstantPayWorksProps> = ({
  setLastCompletedStep,
}) => {
  const [selectedCardId, setSelectedCardId] = useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isMobile = useIsMobile();

  const scrollRightOnDesktop = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: 360, behavior: "smooth" });
    }
  };

  const cards: Array<HowInstantPayWorksType> = [
    {
      id: 1,
      title: "Scan & Pay",
      icon: <ScanIcon width={ICON_WIDTH} height={ICON_HEIGHT} />,
      note: "Make sure to tell the customer how much they should pay you and that your Open Rewards QR code is easily accessible",
      onNext: () => {
        setSelectedCardId(2);
      },
      description:
        "Your customer will scan your business's Open Rewards QR code and enter the amount of their bill to pay.",
    },
    {
      id: 2,
      title: "Receive & Track Payment",
      icon: <PaymentIcon width={ICON_WIDTH} height={ICON_HEIGHT} />,
      note: "It is always advised to check the notification from us to confirm that the payment went through.",
      onNext: () => {
        setSelectedCardId(3);
      },
      description:
        "Once the customer pays, the amount is credited to your Rewards Funds Balance in real time. You’ll also receive a notification confirming the transaction.",
    },
    {
      id: 3,
      title: "Check Your Balance",
      icon: <MoneyIcon width={ICON_WIDTH} height={ICON_HEIGHT} />,
      note: "Regularly check your balance to monitor earnings!",
      onNext: () => {
        setSelectedCardId(4);
      },
      description:
        "Go to the Rewards Funds Balance section on your homepage to track all received payments.",
    },
    {
      id: 4,
      title: "Withdraw Your Money",
      icon: <WithDrawMoneyIcon width={ICON_WIDTH} height={ICON_HEIGHT} />,
      note: "Ensure your payout details are up-to-date for a smooth withdrawal!",
      onNext: () => {
        setSelectedCardId(5);
        setLastCompletedStep(1);
      },
      description:
        "Click “Transfer Funds”, select your payout method, and confirm. The money will be sent to your saved payout option.",
    },
  ];

  useEffect(() => {
    if (
      isMobile &&
      selectedCardId > 1 &&
      cardRefs.current[selectedCardId - 1]
    ) {
      cardRefs.current[selectedCardId - 1]?.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    } else if (containerRef.current && selectedCardId > 1) {
      scrollRightOnDesktop();
    }
  }, [selectedCardId]);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="text-black80 text-xs">
        To ensure that your customers have a smooth and positive experience with
        Instant Pay, we ask that our businesses review this tutorial and
        familiarize with how it works.
      </div>
      <div
        className="flex flex-1 flex-row gap-8 content-center overflow-x-auto w-full h-full no-scrollbar"
        ref={containerRef}
      >
        {cards?.map((card, idx) => {
          return (
            <div
              key={card.id}
              className="flex"
              ref={(el) => (cardRefs.current[idx] = el)}
            >
              <TutorialCard card={card} selectedCardId={selectedCardId} />
            </div>
          );
        })}
      </div>
      <StepIndicators steps={cards} lastCompletedStep={selectedCardId - 1} />
    </div>
  );
};

export default HowInstantPayWorks;
